import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users, Building2, TrendingUp, DollarSign, Wrench } from 'lucide-react';
import { MetricCard } from '../components/dashboard/MetricCard';
import { RevenueChart } from '../components/dashboard/RevenueChart';
import { OccupancyChart } from '../components/dashboard/OccupancyChart';
import { Card } from '../components/ui/Card';
import { Table, Column } from '../components/ui/Table';
import { api } from '../services/api';
import { formatDate } from '../utils/date';
import {
  Property,
  Investment,
  DashboardMetrics,
  OccupancyData,
  MaintenanceDashboardMetrics,
} from '../types';

const DEFAULT_METRICS: DashboardMetrics = {
  totalInvestors: 0,
  investorsChange: null,
  activeProperties: 0,
  averageOccupancy: 0,
  occupancyChange: null,
  monthlyRevenue: 0,
  expectedMonthlyRevenue: 0,
  collectionRate: null,
  partialPayments: 0,
  revenueChange: null,
};

const DEFAULT_MAINTENANCE: MaintenanceDashboardMetrics = {
  statusCounts: { pending: 0, in_progress: 0, reviewed: 0, resolved: 0 },
  totalOpen: 0,
  unassigned: 0,
  recentActivity: 0,
};

function getDateRange(range: string) {
  const to = new Date();
  const from = new Date();
  switch (range) {
    case 'month':
      from.setMonth(from.getMonth() - 1);
      break;
    case 'quarter':
      from.setMonth(from.getMonth() - 3);
      break;
    case 'year':
      from.setFullYear(from.getFullYear() - 1);
      break;
    default:
      from.setMonth(from.getMonth() - 1);
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

function toDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseDateInputValue(v: string): Date | null {
  // v: YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;
  const [y, m, d] = v.split('-').map(Number);
  const out = new Date(y, m - 1, d);
  if (Number.isNaN(out.getTime())) return null;
  return out;
}

function getPresetRange(preset: 'month' | 'quarter' | 'year'): { from: string; to: string } {
  return getDateRange(preset);
}

async function fetchDashboardData(params: { from: string; to: string }) {
  const TIMEOUT_MS = 20000;
  const withTimeout = <T,>(promise: Promise<T>, label: string): Promise<T> =>
    Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(() => reject(new Error(`Timeout: ${label}`)), TIMEOUT_MS),
      ),
    ]);

  const { from, to } = params;

  try {
    const allData = await withTimeout(api.getDashboardAll({ from, to }), 'dashboard/all');
    return {
      metrics: allData.metrics,
      revenue: allData.revenue || [],
      occupancy: allData.occupancy || [],
      properties: allData.properties || [],
      investments: allData.investments || [],
      revenueByProperty: allData.revenueByProperty ?? {},
      maintenance: allData.maintenance ?? DEFAULT_MAINTENANCE,
      error: null as string | null,
    };
  } catch {
    const [metricsRes, revenueRes, occupancyRes, propsRes, invsRes] = await Promise.allSettled([
      withTimeout(api.getDashboardMetrics(), 'metrics'),
      withTimeout(api.getRevenueData(), 'revenue'),
      withTimeout(api.getOccupancyData(), 'occupancy'),
      withTimeout(api.getProperties({ limit: 500 }), 'properties'),
      withTimeout(api.getInvestments({ limit: 500 }), 'investments'),
    ]);

    const metrics = metricsRes.status === 'fulfilled' ? metricsRes.value : null;
    const revenue = revenueRes.status === 'fulfilled' ? revenueRes.value : [];
    const occupancy = occupancyRes.status === 'fulfilled' ? occupancyRes.value : [];
    const props = propsRes.status === 'fulfilled' && propsRes.value?.data != null ? propsRes.value.data : [];
    const invs = invsRes.status === 'fulfilled' && invsRes.value?.data != null ? invsRes.value.data : [];

    const failed = [metricsRes, revenueRes, occupancyRes, propsRes, invsRes].filter((r) => r.status === 'rejected');
    return {
      metrics: metrics || DEFAULT_METRICS,
      revenue,
      occupancy,
      properties: props,
      investments: invs,
      revenueByProperty: {} as Record<string, number>,
      maintenance: DEFAULT_MAINTENANCE,
      error: failed.length > 0 ? 'Algunos datos no cargaron. Puedes actualizar para reintentar.' : null,
    };
  }
}

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<'month' | 'quarter' | 'year' | 'custom'>('month');
  const [customFrom, setCustomFrom] = useState(() => {
    const now = new Date();
    const from = new Date(now);
    from.setMonth(from.getMonth() - 1);
    return toDateInputValue(from);
  });
  const [customTo, setCustomTo] = useState(() => toDateInputValue(new Date()));

  const effectiveRange = useMemo(() => {
    if (preset !== 'custom') return getPresetRange(preset);

    const fromD = parseDateInputValue(customFrom);
    const toD = parseDateInputValue(customTo);
    if (!fromD || !toD) {
      return getPresetRange('month');
    }
    // Normalizar para incluir el día completo del "hasta"
    const from = new Date(fromD.getFullYear(), fromD.getMonth(), fromD.getDate(), 0, 0, 0, 0);
    const to = new Date(toD.getFullYear(), toD.getMonth(), toD.getDate(), 23, 59, 59, 999);
    if (from > to) {
      return { from: to.toISOString(), to: from.toISOString() };
    }
    return { from: from.toISOString(), to: to.toISOString() };
  }, [preset, customFrom, customTo]);

  const { data, isLoading, isError, error: queryError } = useQuery({
    queryKey: ['dashboard', effectiveRange.from, effectiveRange.to],
    queryFn: () => fetchDashboardData(effectiveRange),
    staleTime: 60 * 1000,
  });

  const metrics = data?.metrics ?? null;
  const revenueData = data?.revenue ?? [];
  const occupancyData = (data?.occupancy ?? []) as OccupancyData[];
  const properties = (data?.properties ?? []) as Property[];
  const investments = (data?.investments ?? []) as Investment[];
  const revenueByProperty = data?.revenueByProperty ?? {};
  const maintenance = data?.maintenance ?? DEFAULT_MAINTENANCE;
  const loading = isLoading;
  const error: string | null =
    data?.error ?? (isError && queryError ? (queryError as Error)?.message ?? null : null);

  const parseAmount = (value: unknown): number => {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    const n = Number(value);
    return Number.isNaN(n) ? 0 : n;
  };

  const occupancyByPropertyId = new Map(
    occupancyData.map((o) => [o.propertyId, o] as const),
  );

  const rentCollected = (propertyId: string) => Number(revenueByProperty[propertyId]) || 0;

  const topProperties = [...properties]
    // Evitar confusión: en el dashboard general solo rankeamos propiedades en operación
    .filter((p) => (p as any)?.status === 'operativo')
    .sort((a, b) => rentCollected(b.id) - rentCollected(a.id))
    .slice(0, 5);

  const recentInvestments = [...investments]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  const formatCurrency = (value: number) =>
    value.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const revenueCardTitle =
    preset === 'month'
      ? 'Ingresos del Mes'
      : preset === 'quarter'
        ? 'Ingresos del Trimestre'
        : 'Ingresos del Año';

  const revenueChartSubtitle = useMemo(() => {
    const rows = Array.isArray(data?.revenue) ? data.revenue : [];
    if (rows.length === 0) return undefined;
    if (rows.length === 1) return rows[0]?.month;
    const first = rows[0]?.month;
    const last = rows[rows.length - 1]?.month;
    return first && last ? `${first} — ${last}` : undefined;
  }, [data?.revenue]);

  const propertyColumns: Column<Property>[] = [
    { header: 'Propiedad', accessor: 'name' },
    { header: 'Ciudad', accessor: 'city' },
    {
      header: 'Renta cobrada',
      accessor: (row) => formatCurrency(rentCollected(row.id)),
    },
    {
      header: 'Inversión activa',
      accessor: (row) => {
        const total = investments
          .filter((inv) => inv.propertyId === row.id && inv.status === 'active')
          .reduce((sum, inv) => sum + parseAmount(inv.amount), 0);
        return formatCurrency(total);
      },
    },
    {
      header: 'Ocupación',
      accessor: (row) => {
        const o = occupancyByPropertyId.get(row.id);
        if (o) {
          const total = o.occupied + o.vacant;
          return total > 0 ? `${Math.round((o.occupied / total) * 100)}%` : '0%';
        }
        return row.unitCount ? `${Math.round((row.occupiedUnits / row.unitCount) * 100)}%` : '0%';
      },
    },
  ];

  const investmentColumns: Column<Investment>[] = [
    { header: 'Inversionista', accessor: 'investorName' },
    { header: 'Propiedad', accessor: 'propertyName' },
    {
      header: 'Monto',
      accessor: (row) => formatCurrency(parseAmount(row.amount)),
    },
    {
      header: 'Participación',
      accessor: (row) => `${row.ownershipPct}%`,
    },
    {
      header: 'Fecha',
      accessor: (row) => formatDate(row.createdAt),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard General</h1>
        <div className="flex flex-col gap-2 mt-4 sm:mt-0 items-start sm:items-end">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden">
            {(
              [
                { id: 'month' as const, label: 'Mes' },
                { id: 'quarter' as const, label: 'Trimestre' },
                { id: 'year' as const, label: 'Año' },
                { id: 'custom' as const, label: 'Personalizado' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                disabled={loading}
                onClick={() => setPreset(opt.id)}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  preset === opt.id ? 'bg-primary text-white' : 'text-gray-700 hover:bg-gray-50'
                } disabled:opacity-50`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {preset === 'custom' && (
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-sm text-gray-700 flex items-center gap-2">
                Desde
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  disabled={loading}
                  className="px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </label>
              <label className="text-sm text-gray-700 flex items-center gap-2">
                Hasta
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  disabled={loading}
                  className="px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </label>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {loading && !metrics ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Cargando datos del dashboard...</p>
        </div>
      ) : (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Inversionistas"
              value={metrics?.totalInvestors || 0}
              change={metrics?.investorsChange ?? undefined}
              icon={Users}
              onClick={() => navigate('/inversionistas')}
            />
            <MetricCard
              title="Propiedades Activas"
              value={metrics?.activeProperties || 0}
              icon={Building2}
              onClick={() => navigate('/propiedades')}
            />
            <MetricCard
              title="Ocupación Promedio"
              value={`${metrics?.averageOccupancy || 0}%`}
              change={metrics?.occupancyChange ?? undefined}
              changeUnit=" p.p."
              icon={TrendingUp}
            />
            <MetricCard
              title={revenueCardTitle}
              value={formatCurrency(Number(metrics?.monthlyRevenue) || 0)}
              change={metrics?.revenueChange ?? undefined}
              icon={DollarSign}
              subtitle={
                metrics?.collectionRate != null && metrics?.expectedMonthlyRevenue != null
                  ? `${metrics.collectionRate}% de ${formatCurrency(Number(metrics.expectedMonthlyRevenue))} esperado`
                  : undefined
              }
              warningText={
                (metrics?.partialPayments ?? 0) > 0
                  ? `${metrics?.partialPayments ?? 0} pago(s) parcial(es)`
                  : undefined
              }
            />
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" aria-hidden />
              Mantenimiento
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <p className="text-sm text-gray-600 mb-1">Reportes abiertos</p>
                <p className="text-2xl font-bold text-primary">{maintenance.totalOpen}</p>
                <p className="text-xs text-gray-500 mt-2">
                  {maintenance.statusCounts.pending} pendientes ·{' '}
                  {maintenance.statusCounts.in_progress} en progreso ·{' '}
                  {maintenance.statusCounts.reviewed} en revisión
                </p>
              </Card>
              <Card
                className={
                  maintenance.unassigned > 0
                    ? 'border-amber-300 bg-amber-50'
                    : 'border-emerald-200 bg-emerald-50'
                }
              >
                <p className="text-sm text-gray-600 mb-1">Sin asignar</p>
                <p
                  className={`text-2xl font-bold ${
                    maintenance.unassigned > 0 ? 'text-amber-800' : 'text-emerald-800'
                  }`}
                >
                  {maintenance.unassigned}
                </p>
                <p className="text-xs text-gray-600 mt-2">
                  {maintenance.unassigned > 0
                    ? 'Reportes sin técnico asignado'
                    : 'Todo asignado'}
                </p>
              </Card>
              <Card>
                <p className="text-sm text-gray-600 mb-1">Actividad (7 días)</p>
                <p className="text-2xl font-bold text-primary">{maintenance.recentActivity}</p>
                <p className="text-xs text-gray-500 mt-2">Comentarios de técnicos</p>
              </Card>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RevenueChart data={revenueData} subtitle={revenueChartSubtitle} />
            <OccupancyChart data={occupancyData} />
          </div>
        </>
      )}

      {/* Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Top 5 propiedades (por renta cobrada)">
          <p className="text-xs text-gray-500 mb-3">
            Ordenado por renta cobrada según pagos marcados como <span className="font-medium">pagados</span> en el periodo seleccionado.
            Solo incluye propiedades en <span className="font-medium">Operación</span>.
          </p>
          {topProperties.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">No hay propiedades en operación con renta cobrada en este periodo.</p>
          ) : (
            <Table
              data={topProperties}
              columns={propertyColumns}
              onRowClick={(row) => navigate(`/propiedades/${row.id}`)}
            />
          )}
        </Card>

        <Card title="Últimas Inversiones">
          <Table
            data={recentInvestments}
            columns={investmentColumns}
            onRowClick={(row) => navigate(`/inversionistas/${row.investorId}`)}
          />
        </Card>
      </div>
    </div>
  );
};

